/**
 * Iris Portal — Centralized Error Code System
 *
 * Format: IRIS-NNN
 *   1xx  Authentication / Authorization
 *   2xx  Audit pipeline
 *   3xx  Data / Supabase operations
 *   4xx  Configuration
 *   5xx  Network / Server
 */

const ERRORS = {
  // ── Auth ────────────────────────────────────────────
  'IRIS-100': 'No active session. Please sign in again.',
  'IRIS-101': 'Your email is not authorized to access this portal. Contact Iris Digital Consulting.',
  'IRIS-102': 'No client account is linked to your email. Contact Iris Digital Consulting.',
  'IRIS-103': 'Login timed out. Please refresh and try again.',
  'IRIS-104': 'Google sign-in failed. Please try again.',

  // ── Audit pipeline ─────────────────────────────────
  'IRIS-200': 'Set a website URL for this client before running an audit.',
  'IRIS-201': 'Could not trigger the audit. Is the audit server running?',
  'IRIS-202': 'Audit timed out. Try again or check server logs.',
  'IRIS-203': 'Invalid audit JSON file. Check the format and try again.',
  'IRIS-204': 'Audit server is unreachable at the configured URL.',
  'IRIS-205': 'Audit failed on the server. Check server logs for details.',

  // ── Data / Supabase ────────────────────────────────
  'IRIS-300': 'Failed to load data. Please refresh.',
  'IRIS-301': 'Failed to save. Please try again.',
  'IRIS-302': 'File upload failed.',
  'IRIS-303': 'Failed to delete. Please try again.',
  'IRIS-304': 'Loading timed out. Please refresh.',

  // ── Configuration ──────────────────────────────────
  'IRIS-400': 'Missing Supabase configuration. Check environment variables.',
  'IRIS-401': 'Database column is missing. A migration may be needed.',

  // ── Network / Server ───────────────────────────────
  'IRIS-500': 'Network request failed. Check your connection.',
  'IRIS-501': 'Server returned an unexpected error.',
};

/**
 * Build a user-facing error string with code prefix.
 *
 * @param {string} code    – e.g. 'IRIS-201'
 * @param {string} [detail] – optional extra context (appended after the message)
 * @returns {string}        – e.g. '[IRIS-201] Could not trigger the audit. …'
 */
export function irisError(code, detail) {
  const base = ERRORS[code] || 'An unexpected error occurred.';
  const msg = `[${code}] ${base}`;
  return detail ? `${msg} (${detail})` : msg;
}

/**
 * Map a raw Supabase / fetch error into a coded message.
 * Useful in catch blocks to auto-classify common failures.
 *
 * @param {Error|string} err   – the caught error
 * @param {string} fallbackCode – code to use if nothing specific matches
 * @returns {string}
 */
export function classifyError(err, fallbackCode = 'IRIS-501') {
  const msg = typeof err === 'string' ? err : err?.message || '';

  if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('ERR_CONNECTION'))
    return irisError('IRIS-500', msg);
  if (msg.includes('entity_profile') || msg.includes('42703'))
    return irisError('IRIS-401', msg);
  if (msg.includes('timeout') || msg.includes('Timeout'))
    return irisError('IRIS-304', msg);

  return irisError(fallbackCode, msg);
}

export default ERRORS;
